import { useField } from 'informed';

import MuiTextField from '@mui/material/TextField';
import { inputClasses } from '@mui/material/Input';
import { SxProps, Theme } from '@mui/material';
import { formControlSx, inputLabelSx } from '../FormControlStyled/FormControlStyled';

interface Props {
  id?: string;
  name: string;
  label?: string;
  placeholder?: string;
  value?: string | number;
  type?: string;
  readOnly?: boolean;
  sxModifier?: string;
  FormHelperTextSx?: SxProps;
  maxLength?: string;
  minLength?: string;
  required?: boolean;
  maskOnBlur?: boolean;
  autoComplete?: string;
  validate?: ((value: string, values: Record<string, unknown>) => string | void) | undefined;
  asyncValidate?: ((value: string) => string | void) | undefined;
  validateWhen?: string[];
  validateDeps?: string[];
  pattern?: string | undefined;
  formatter?: ((value: any, ogValue: any) => any) | string | (string | RegExp)[];
  mask?: ((value: any, ogValue: any) => any) | string;
  parser?: (value: any) => number | undefined;
  inputMode?:
    | 'text'
    | 'search'
    | 'email'
    | 'tel'
    | 'url'
    | 'none'
    | 'numeric'
    | 'decimal'
    | undefined;
  tabindex?: string;
  inputCustomSx?: SxProps;
}

export const inputSx = (theme: Theme, sxModifier: string = '') => ({
  fontSize: '1.4rem',
  borderBottomWidth: 0,

  ['&:hover']: {
    ['&:before']: {
      border: 'none !important',
    },
  },

  ['&:before']: {
    borderBottomWidth: 0,
  },

  ['&:after']: {
    borderRadius: '1rem',
  },

  [`.${inputClasses.input}`]: {
    height: '1.8rem',
    // padding: ".2rem 0 .6rem",

    [theme.breakpoints.up('sm')]: {
      // padding: "1.2rem 0 1.4rem",
    },
  },

  [theme.breakpoints.up('sm')]: {
    fontSize: '2rem',
  },

  ...(sxModifier === 'bankAccountPrefix' && {
    // paddingLeft: ".8rem",
    marginTop: '1.6rem',

    [theme.breakpoints.up('sm')]: {
      fontSize: '2rem',
      // paddingLeft: "1.5rem",
    },
  }),

  ...(sxModifier === 'phonePrefix' && {
    paddingLeft: '.8rem',
    marginTop: '1.6rem',
  }),
});

const TextField = ({
  id: propsId,
  label,
  placeholder,
  name,
  value,
  type: propsType,
  readOnly,
  sxModifier,
  FormHelperTextSx,
  maxLength,
  minLength,
  autoComplete,
  required,
  validate,
  asyncValidate,
  validateWhen,
  validateDeps,
  pattern,
  formatter,
  parser,
  inputMode,
  mask,
  maskOnBlur,
  tabindex,
  inputCustomSx,
}: Props) => {
  const type = propsType || 'text';

  /*const validateOn = 'change';
    const showErrorIfError = true;*/

  const {
    render,
    ref,
    informed,
    fieldApi: { setValue },
    fieldState: { error, touched },
  } = useField({
    type,
    name,
    required,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-expect-error
    validate,
    asyncValidate,
    validateWhen,
    formatter,
    parser,
    mask,
    maskOnBlur,
    validateDeps,
    // keepStateIfRelevant: true,
  });

  const id = propsId || name;

  const hasErrorAndTouched = !!error && touched;

  return render(
    <MuiTextField
      inputRef={ref}
      variant="outlined"
      id={id}
      label={label || (hasErrorAndTouched ? '*' : '')}
      defaultValue={value}
      error={hasErrorAndTouched}
      helperText={hasErrorAndTouched ? error.toString() : ''}
      type={type}
      {...(placeholder && { placeholder })}
      {...(required && { required })}
      {...informed}
      onChange={(e) => {
        setValue(e.target.value, e);
      }}
      inputProps={{
        ...(maxLength && { maxLength }),
        ...(minLength && { minLength }),
        ...(autoComplete && { autoComplete }),
        ...(pattern && { pattern }),
        ...(inputMode && { inputMode }),
        ...(tabindex && { tabIndex: Number(tabindex) }),
      }}
      InputLabelProps={{
        sx: inputLabelSx,
      }}
      InputProps={{
        sx: { ...(theme) => inputSx(theme, sxModifier), ...inputCustomSx },
        ...{ readOnly },
      }}
      FormHelperTextProps={{
        sx: {
          position: 'absolute',
          padding: '0 3px',
          left: '-5px',
          bottom: '-10px',
          backgroundColor: '#fff',
          ...FormHelperTextSx,
        },
      }}
      sx={{
        '& .MuiOutlinedInput-notchedOutline legend': {
          display: 'none',
        },
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: 'var(--E4E4E4)', // Change this to your desired color
          },
        },
        ...formControlSx,
      }}
    />,
  );
};

export default TextField;
